// useAuth.js
import { useEffect, useRef, useState } from "react";
import { ensureGrafanaLogin, supabase } from "../components/SupabaseClient";

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const mergePreservedRole = (baseUser, prev, fallbackRole) => {
      if (!prev?.userRole || prev.id !== baseUser.id) {
        return fallbackRole
          ? { ...baseUser, userRole: fallbackRole }
          : baseUser;
      }
      return { ...baseUser, userRole: prev.userRole };
    };

    // this function embeds user role into user object
    const enrichUserRole = async (baseUser) => {
      if (!baseUser) return null;

      // set the timeout for the RPC call, 3 seconds
      const ac = new AbortController();
      const t = setTimeout(() => ac.abort(), 3000);

      try {
        const { data, error } = await supabase.rpc(
          "is_admin",
          {},
          { signal: ac.signal },
        );
        clearTimeout(t);
        const role = error ? "user" : data === true ? "admin" : "user";

        if (!mountedRef.current) return;
        setUser({ ...baseUser, userRole: role });
        if (role === "admin") {
          ensureGrafanaLogin().catch((err) => {
            console.error("[useAuth] Grafana login bootstrap failed:", err);
          });
        }
      } catch (e) {
        // TODO: if timeout, let the user know, instead of silently defaulting to "user"
        clearTimeout(t);
        if (!mountedRef.current) return;
        setUser({ ...baseUser, userRole: "user" });
        console.error("[useAuth] is_admin RPC failed:", e);
      }
    };

    const applySession = async (session, reason) => {
      const baseUser = session?.user ?? null;

      if (!baseUser) {
        if (!mountedRef.current) return;
        setUser(null);
        return;
      }

      // Keep any previously resolved role for the same user while refreshing it.
      if (!mountedRef.current) return;
      setUser((prev) => mergePreservedRole(baseUser, prev));
      enrichUserRole(baseUser);
      console.log("[useAuth]", reason, baseUser.email);
    };

    (async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) console.error("[useAuth] getSession error:", error);
        await applySession(data?.session, "getSession");
      } catch (e) {
        console.error("[useAuth] getSession threw:", e);
        setUser(null);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("[useAuth] onAuthStateChange:", event);
      await applySession(session, event);
    });

    return () => {
      mountedRef.current = false;
      subscription?.unsubscribe();
    };
  }, []);

  return { user, loading };
};
